import { OnboardingStep, TenantConfigSection } from '@today/types'
import type { ComponentType } from 'react'
import type { TenantConfigSectionProps } from '@/modules/tenant-config/section-props'
import { TENANT_CONFIG_SECTION_REGISTRY } from '@/modules/tenant-config/section-registry'

/** How a wizard step shows its content (TO-190 REQ-001). */
export enum WizardStepKind {
	/** Renders an existing tenant-config section inline. */
	EMBEDDED = 'EMBEDDED',
	/** Links out to an existing standalone page. */
	LINK_OUT = 'LINK_OUT',
}

export interface OnboardingWizardLink {
	label: string
	href: string
}

interface WizardStepBase {
	title: string
	description: string
}

export type OnboardingWizardStep =
	| (WizardStepBase & {
			kind: WizardStepKind.EMBEDDED
			section: TenantConfigSection
			component: ComponentType<TenantConfigSectionProps>
	  })
	| (WizardStepBase & { kind: WizardStepKind.LINK_OUT; links: (slug: string) => OnboardingWizardLink[] })

export type OnboardingWizardRegistry = Record<OnboardingStep, OnboardingWizardStep>

/** An embedded step reuses the console's registered section component. */
const embedded = (section: TenantConfigSection, description: string): OnboardingWizardStep => {
	const { title, component } = TENANT_CONFIG_SECTION_REGISTRY[section]
	if (!component) throw new Error(`Tenant-config section ${section} has no component to embed`)
	return { title, description, kind: WizardStepKind.EMBEDDED, section, component }
}

const slugPath = (slug: string) => `/t/${encodeURIComponent(slug)}`

/**
 * The wizard's steps (TO-190 REQ-001). The shell holds no step logic: each step
 * is an existing section or page. A new step = an `OnboardingStep` member, a
 * catalogue entry in `@today/types`, and an entry here.
 */
export const ONBOARDING_WIZARD_REGISTRY: OnboardingWizardRegistry = {
	[OnboardingStep.IDENTITY]: {
		title: 'Identity & login',
		description: 'Connect your identity provider and map its attributes and roles, so your users can sign in.',
		kind: WizardStepKind.LINK_OUT,
		links: (slug) => [{ label: 'Open SSO settings', href: `${slugPath(slug)}/admin/sso` }],
	},
	[OnboardingStep.LOCALES]: {
		title: 'Locales',
		description: 'Choose the languages your content is published in, which ones are required, and the default.',
		kind: WizardStepKind.LINK_OUT,
		links: (slug) => [{ label: 'Open locale settings', href: `${slugPath(slug)}/admin/locale-settings` }],
	},
	[OnboardingStep.ORGANIZATION]: embedded(
		TenantConfigSection.ORGANIZATION,
		'Set up your organization units and their hierarchy.',
	),
	[OnboardingStep.FILE_EXCHANGE]: embedded(
		TenantConfigSection.FILE_EXCHANGE,
		'Configure how files are exchanged with your systems.',
	),
	[OnboardingStep.CONTENT_REVIEW]: {
		title: 'First content review',
		description: 'Look over the sample product and the legal texts created for your tenant.',
		kind: WizardStepKind.LINK_OUT,
		links: () => [
			{ label: 'Review products', href: '/products' },
			{ label: 'Review legal texts', href: '/content/texts' },
		],
	},
}
